import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Stage, Layer, Rect, Text, Image as KImage, Group, Transformer } from "react-konva";
import useImage from "use-image";
import api, { fileUrl, BACKEND_URL } from "@/lib/api";
import { FACE_FILTERS } from "@/lib/sketchify";
import Navbar from "@/components/Navbar";
import { toast } from "sonner";
import { Plus, Trash2, Type, Image as ImageIcon, Upload, Save, Globe, ArrowLeft, Copy, X, Layers as LayersIcon, Eye, Grid3x3, Files } from "lucide-react";

const CANVAS_SIZE = 1000;
const uid = () => Math.random().toString(36).slice(2, 10);

// Text measurement + auto-fit (mirrors Customizer)
let _measureCtx;
function measureText(text, fontSize, fontFamily) {
  if (!_measureCtx) _measureCtx = document.createElement("canvas").getContext("2d");
  _measureCtx.font = `${fontSize}px "${fontFamily}", system-ui, sans-serif`;
  return _measureCtx.measureText(text).width;
}
// Line-height multiplier applied to every wrapped multi-line block. Mirrors the Konva <Text
// lineHeight/> prop so measured height === rendered height.
const LINE_HEIGHT = 1.2;
// Wrap text into visual lines that fit maxWidth. Honors hard `\n` newlines, then greedily
// word-wraps each paragraph; force-splits any single word wider than maxWidth at the character
// level so text never overflows horizontally. Always returns at least one line.
function wrapLines(text, fontSize, fontFamily, maxWidth) {
  const limit = Math.max(1, maxWidth * 0.94);
  const paragraphs = String(text || "").split("\n");
  const out = [];
  for (const para of paragraphs) {
    if (!para) { out.push(""); continue; }
    const words = para.split(/(\s+)/);
    let cur = "";
    const pushCur = () => { if (cur.length) { out.push(cur); cur = ""; } };
    for (const w of words) {
      if (!w) continue;
      const trial = cur + w;
      if (measureText(trial, fontSize, fontFamily) <= limit) { cur = trial; continue; }
      pushCur();
      if (measureText(w, fontSize, fontFamily) <= limit) {
        cur = /^\s+$/.test(w) ? "" : w;
      } else {
        let chunk = "";
        for (const ch of w) {
          if (measureText(chunk + ch, fontSize, fontFamily) <= limit) { chunk += ch; }
          else { if (chunk) out.push(chunk); chunk = ch; }
        }
        cur = chunk;
      }
    }
    out.push(cur);
  }
  return out.length ? out : [""];
}
function computeFontSize(text, width, height, fontFamily, min = 12, max = 240) {
  if (!text) return min;
  const upper = Math.min(max, Math.floor(height * 0.95));
  let lo = min, hi = upper, best = min;
  while (lo <= hi) {
    const mid = Math.floor((lo + hi) / 2);
    const lines = wrapLines(text, mid, fontFamily, width);
    const totalH = lines.length * mid * LINE_HEIGHT;
    if (totalH <= height * 0.98) { best = mid; lo = mid + 1; }
    else { hi = mid - 1; }
  }
  return Math.max(min, best);
}

// Curved text renderer (mirrors Customizer)
function CurvedText({ text, width, height, curve, fontFamily, fontSize, fill }) {
  const cx = width / 2, midY = height / 2;
  const amt = curve / 100;
  const apex = amt * (height * 0.45);
  const startX = 0, endX = width;
  const startY = midY + apex, endY = midY + apex, apexY = midY - apex;
  const samples = 60;
  let arcLen = 0, prevX = startX, prevY = startY;
  for (let i = 1; i <= samples; i++) {
    const t = i / samples, mt = 1 - t;
    const x = mt * mt * startX + 2 * mt * t * cx + t * t * endX;
    const y = mt * mt * startY + 2 * mt * t * apexY + t * t * endY;
    arcLen += Math.hypot(x - prevX, y - prevY); prevX = x; prevY = y;
  }
  const chars = Array.from(text);
  if (chars.length === 0) return null;
  const total = measureText(text, fontSize, fontFamily);
  const usedWidth = Math.min(total, arcLen * 0.98);
  const startOffset = (arcLen - usedWidth) / 2;
  const charWidths = chars.map((ch) => measureText(ch, fontSize, fontFamily));
  const arcPoint = (s) => {
    let acc = 0, pX = startX, pY = startY;
    for (let i = 1; i <= samples; i++) {
      const t = i / samples, mt = 1 - t;
      const x = mt * mt * startX + 2 * mt * t * cx + t * t * endX;
      const y = mt * mt * startY + 2 * mt * t * apexY + t * t * endY;
      const seg = Math.hypot(x - pX, y - pY);
      if (acc + seg >= s) {
        const r = (s - acc) / (seg || 1);
        return { x: pX + (x - pX) * r, y: pY + (y - pY) * r, angle: Math.atan2(y - pY, x - pX) };
      }
      acc += seg; pX = x; pY = y;
    }
    return { x: startX, y: startY, angle: 0 };
  };
  let cursor = startOffset;
  return (
    <>
      {chars.map((ch, i) => {
        const cw = charWidths[i], s = cursor + cw / 2, p = arcPoint(s);
        cursor += cw;
        return (
          <Text key={i} text={ch} x={p.x} y={p.y}
            offsetX={cw / 2} offsetY={fontSize / 2}
            rotation={(p.angle * 180) / Math.PI}
            fontFamily={fontFamily} fontSize={fontSize} fill={fill}/>
        );
      })}
    </>
  );
}

function BaseImage({ url }) {
  const [img] = useImage(url, "anonymous");
  if (!img) return null;
  return <KImage image={img} width={CANVAS_SIZE} height={CANVAS_SIZE} listening={false} />;
}

async function loadCustomFonts(customFonts) {
  if (!customFonts || !document.fonts) return;
  for (const cf of customFonts) {
    const family = cf.name;
    if ([...document.fonts].some((f) => f.family === family)) continue;
    try {
      const url = cf.url.startsWith("http") ? cf.url : `${BACKEND_URL}${cf.url}`;
      const face = new FontFace(family, `url(${url})`);
      const loaded = await face.load();
      document.fonts.add(loaded);
    } catch (e) { /* ignore */ }
  }
}

export default function TemplateEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [tpl, setTpl] = useState(null);
  const [activeView, setActiveView] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [customFontList, setCustomFontList] = useState([]);
  const [googleFontList, setGoogleFontList] = useState([]);
  const [duplicating, setDuplicating] = useState(false);
  const [gridOn, setGridOn] = useState(() => localStorage.getItem("cc_editor_grid_on") === "1");
  const stageRef = useRef(null);
  const trRef = useRef(null);
  const shapeRefs = useRef({});
  const [scale, setScale] = useState(0.6);

  useEffect(() => {
    localStorage.setItem("cc_editor_grid_on", gridOn ? "1" : "0");
  }, [gridOn]);

  const duplicate = async () => {
    setDuplicating(true);
    try {
      const { data } = await api.post(`/templates/${id}/duplicate`);
      toast.success("Template duplicated");
      nav(`/templates/${data.id}/edit`);
    } catch { toast.error("Duplicate failed"); }
    setDuplicating(false);
  };

  useEffect(() => {
    api.get(`/templates/${id}`).then((r) => {
      const t = r.data;
      if (!t.views || t.views.length === 0) {
        t.views = [{ id: "view-front", name: "Front", base_image_url: t.base_image_url || "", bg_color: t.bg_color || "#ffffff", zones: t.zones || [] }];
      }
      setTpl(t);
    }).catch(() => { toast.error("Template not found"); nav("/dashboard"); });
    api.get("/fonts").then(async (r) => {
      setGoogleFontList(r.data.google_fonts || []);
      const cf = r.data.custom_fonts || [];
      setCustomFontList(cf);
      await loadCustomFonts(cf);
    });
  }, [id, nav]);

  useEffect(() => {
    const fit = () => {
      const w = Math.min(window.innerWidth - 420, 700);
      setScale(Math.max(0.3, w / CANVAS_SIZE));
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, []);

  useEffect(() => {
    if (!selectedId || !trRef.current) return;
    const node = shapeRefs.current[selectedId];
    if (node) { trRef.current.nodes([node]); trRef.current.getLayer().batchDraw(); }
    else { trRef.current.nodes([]); }
  }, [selectedId, tpl, activeView]);

  if (!tpl) return (<div className="min-h-screen"><Navbar/><div className="p-10">Loading…</div></div>);

  const views = tpl.views || [];
  const view = views[activeView] || views[0];

  const setField = (k, v) => setTpl((t) => ({ ...t, [k]: v }));
  const setViewField = (k, v) => setTpl((t) => ({
    ...t,
    views: t.views.map((vv, i) => i === activeView ? { ...vv, [k]: v } : vv),
  }));
  const setZone = (zid, patch) => setTpl((t) => ({
    ...t,
    views: t.views.map((vv, i) => i === activeView ? { ...vv, zones: vv.zones.map((z) => z.id === zid ? { ...z, ...patch } : z) } : vv),
  }));
  const removeZone = (zid) => setTpl((t) => ({
    ...t,
    views: t.views.map((vv, i) => i === activeView ? { ...vv, zones: vv.zones.filter((z) => z.id !== zid) } : vv),
  }));
  const addZone = (type) => {
    const z = {
      id: uid(), type,
      x: 200, y: 300, width: 600, height: type === "text" ? 200 : 400,
      default_text: type === "text" ? "Your text here" : "",
      default_font: "Outfit", default_color: "#1c1917",
      min_font_size: 12, max_font_size: 240,
      curve: 0, allow_curve: true,
      allow_font_change: true, allow_color_change: true,
      allow_upload: true, min_image_dimension: 300,
      customer_editable: true,
      label: type === "text" ? "Text layer" : "Image layer",
    };
    setTpl((t) => ({
      ...t,
      views: t.views.map((vv, i) => i === activeView ? { ...vv, zones: [...vv.zones, z] } : vv),
    }));
    setSelectedId(z.id);
  };

  const addView = () => {
    const nid = `view-${uid()}`;
    setTpl((t) => ({ ...t, views: [...t.views, { id: nid, name: `View ${t.views.length + 1}`, base_image_url: "", bg_color: "#ffffff", zones: [] }] }));
    setActiveView(views.length);
  };
  const removeView = (idx) => {
    if (views.length === 1) { toast.error("At least one view required"); return; }
    if (!window.confirm(`Delete view "${views[idx].name}"?`)) return;
    setTpl((t) => ({ ...t, views: t.views.filter((_, i) => i !== idx) }));
    setActiveView(Math.max(0, activeView - 1));
  };

  const uploadBase = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("kind", "product_base");
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setViewField("base_image_url", fileUrl(data.url));
      toast.success("Base image uploaded");
    } catch { toast.error("Upload failed"); }
    setUploading(false);
  };

  const uploadFont = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("kind", "font");
      await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const r = await api.get("/fonts");
      setCustomFontList(r.data.custom_fonts || []);
      await loadCustomFonts(r.data.custom_fonts || []);
      toast.success("Custom font uploaded & ready to use");
    } catch { toast.error("Font upload failed"); }
  };

  const deleteFont = async (fid) => {
    if (!window.confirm("Remove this font?")) return;
    try {
      await api.delete(`/files/${fid}`);
      const r = await api.get("/fonts");
      setCustomFontList(r.data.custom_fonts || []);
      toast.success("Font removed");
    } catch { toast.error("Failed"); }
  };

  const save = async (andPublish) => {
    setSaving(true);
    try {
      const payload = { ...tpl, published: andPublish ?? tpl.published };
      delete payload.id; delete payload.owner_id; delete payload.created_at; delete payload.updated_at;
      const { data } = await api.put(`/templates/${id}`, payload);
      setTpl(data);
      toast.success(andPublish ? "Published!" : "Saved");
    } catch { toast.error("Save failed"); }
    setSaving(false);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/c/${tpl.id}`);
    toast.success("Public customizer link copied");
  };

  const selected = view?.zones?.find((z) => z.id === selectedId);
  const allFontOptions = [...googleFontList, ...customFontList.map((c) => c.name)];

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <button onClick={() => nav("/dashboard")} className="brutal-btn bg-white px-3 py-2 rounded-md text-sm flex items-center gap-1" data-testid="back-to-dashboard">
              <ArrowLeft className="w-4 h-4"/> Back
            </button>
            <input value={tpl.name} onChange={(e) => setField("name", e.target.value)} className="brutal-input font-heading font-black text-xl w-72" data-testid="tpl-name"/>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setGridOn((v) => !v)}
              className={`brutal-btn px-3 py-2 rounded-md text-sm flex items-center gap-1 ${gridOn ? "bg-lime-300" : "bg-white"}`}
              data-testid="grid-toggle-btn"
              title="Editor-only alignment grid. Never appears on the customer view or in final downloads."
            >
              <Grid3x3 className="w-4 h-4"/> Grid {gridOn ? "ON" : "OFF"}
            </button>
            <button
              onClick={duplicate}
              disabled={duplicating}
              className="brutal-btn bg-orange-200 px-3 py-2 rounded-md text-sm flex items-center gap-1"
              data-testid="duplicate-template-btn"
              title="Create a full copy of this template (all layers, fonts, ink slots, permissions, printable areas). New copy is a draft."
            >
              <Files className="w-4 h-4"/> {duplicating ? "Copying…" : "Duplicate"}
            </button>
            <button
              onClick={() => {
                const token = localStorage.getItem("cc_token") || "";
                const url = `/c/${tpl.id}?preview_token=${encodeURIComponent(token)}`;
                window.open(url, "_blank", "noopener");
              }}
              className="brutal-btn bg-fuchsia-200 px-3 py-2 rounded-md text-sm flex items-center gap-1"
              data-testid="preview-as-customer-btn"
              title="Open the current template in a new tab exactly as your customers will see it — even if it's still a draft."
            >
              <Eye className="w-4 h-4"/> Preview as customer
            </button>
            <button onClick={copyLink} disabled={!tpl.published} className="brutal-btn bg-white px-3 py-2 rounded-md text-sm flex items-center gap-1"><Copy className="w-4 h-4"/> Copy link</button>
            <button onClick={() => save(false)} disabled={saving} className="brutal-btn bg-white px-3 py-2 rounded-md text-sm flex items-center gap-1" data-testid="save-btn"><Save className="w-4 h-4"/> Save</button>
            <button onClick={() => save(!tpl.published)} disabled={saving} className="brutal-btn bg-yellow-300 px-3 py-2 rounded-md text-sm flex items-center gap-1" data-testid="publish-btn">
              <Globe className="w-4 h-4"/> {tpl.published ? "Unpublish" : "Publish"}
            </button>
          </div>
        </div>

        {/* View tabs */}
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          {views.map((v, i) => (
            <div key={v.id} className={`brutal-card px-2 py-1 flex items-center gap-1 ${i === activeView ? 'bg-yellow-200' : 'bg-white'}`} data-testid={`view-tab-${i}`}>
              <button onClick={() => setActiveView(i)} className="text-sm font-bold px-2">{v.name}</button>
              {i === activeView && (
                <input value={v.name} onChange={(e) => setViewField("name", e.target.value)} className="brutal-input text-xs w-24 py-1" data-testid={`view-name-${i}`}/>
              )}
              <button onClick={() => removeView(i)} className="opacity-60 hover:opacity-100" title="Delete view"><X className="w-3 h-3"/></button>
            </div>
          ))}
          <button onClick={addView} className="brutal-btn bg-green-200 px-3 py-1.5 rounded-md text-sm flex items-center gap-1" data-testid="add-view">
            <Plus className="w-3 h-3"/> Add view (front/back/etc.)
          </button>
        </div>

        <div className="grid lg:grid-cols-[1fr_380px] gap-6">
          {/* CANVAS */}
          <div className="brutal-card p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs font-bold uppercase tracking-widest text-stone-600">
                <LayersIcon className="inline w-3 h-3 mr-1"/>{view?.name} · 1000 × 1000
              </div>
              <div className="flex gap-2">
                <button onClick={() => addZone("text")} className="brutal-btn bg-yellow-300 px-3 py-1.5 rounded-md text-sm flex items-center gap-1" data-testid="add-text-zone"><Type className="w-4 h-4"/> Text layer</button>
                <button onClick={() => addZone("image")} className="brutal-btn bg-pink-200 px-3 py-1.5 rounded-md text-sm flex items-center gap-1" data-testid="add-image-zone"><ImageIcon className="w-4 h-4"/> Image layer</button>
              </div>
            </div>
            <div className="flex items-center justify-center bg-stone-100 border-2 border-stone-900 rounded-md overflow-hidden" style={{ minHeight: 400 }}>
              <div style={{ width: CANVAS_SIZE * scale, height: CANVAS_SIZE * scale }}>
                <Stage ref={stageRef} width={CANVAS_SIZE * scale} height={CANVAS_SIZE * scale} scaleX={scale} scaleY={scale}
                  onMouseDown={(e) => { if (e.target === e.target.getStage()) setSelectedId(null); }}>
                  <Layer>
                    <Rect x={0} y={0} width={CANVAS_SIZE} height={CANVAS_SIZE} fill={view?.bg_color || "#ffffff"} />
                    {view?.base_image_url && <BaseImage url={view.base_image_url} />}
                    {gridOn && (() => {
                      // Editor-only alignment grid — every 50px + heavier centre lines. Never shown
                      // to customers (Customizer never reads gridOn) and never exported (this is a
                      // separate Layer above zones but below the transformer; toDataURL is called
                      // on the Customizer stage, not this one).
                      const lines = [];
                      for (let p = 0; p <= CANVAS_SIZE; p += 50) {
                        const heavy = p === 0 || p === CANVAS_SIZE / 2 || p === CANVAS_SIZE;
                        lines.push(<Rect key={`gv-${p}`} x={p - 0.5} y={0} width={1} height={CANVAS_SIZE} fill={heavy ? "#0ea5e9" : "#cbd5e1"} listening={false}/>);
                        lines.push(<Rect key={`gh-${p}`} x={0} y={p - 0.5} width={CANVAS_SIZE} height={1} fill={heavy ? "#0ea5e9" : "#cbd5e1"} listening={false}/>);
                      }
                      return <Group listening={false} opacity={0.55} data-testid="editor-grid-overlay">{lines}</Group>;
                    })()}
                    {(view?.zones || []).map((z) => (
                      <Group key={z.id}
                        ref={(el) => (shapeRefs.current[z.id] = el)}
                        x={z.x} y={z.y} draggable
                        onClick={() => setSelectedId(z.id)}
                        onTap={() => setSelectedId(z.id)}
                        onDragEnd={(e) => setZone(z.id, { x: e.target.x(), y: e.target.y() })}
                        onTransformEnd={(e) => {
                          const node = e.target;
                          const sx = node.scaleX(), sy = node.scaleY();
                          node.scaleX(1); node.scaleY(1);
                          setZone(z.id, { x: node.x(), y: node.y(), width: Math.max(40, z.width * sx), height: Math.max(40, z.height * sy) });
                        }}>
                        <Rect width={z.width} height={z.height}
                          fill={z.customer_editable === false
                            ? "rgba(148,163,184,0.20)"
                            : (z.type === "text" ? "rgba(253,224,71,0.35)" : "rgba(251,207,232,0.35)")}
                          stroke={z.customer_editable === false ? "#64748b" : "#1c1917"}
                          strokeWidth={3}
                          dash={z.customer_editable === false ? [4, 4] : [10, 6]} />
                        {z.type === "image" && z.default_image_url && (
                          <BaseImageInZone url={z.default_image_url} width={z.width} height={z.height}/>
                        )}
                        {z.type === "image" && !z.default_image_url && (
                          <Text width={z.width} height={z.height} align="center" verticalAlign="middle"
                            text="🖼 Image layer" fontFamily="Outfit" fontSize={Math.min(48, z.height/4)} fill="#1c1917" listening={false}/>
                        )}
                        {z.type === "text" && (() => {
                          const txt = z.default_text || "Click to Edit";
                          const font = z.default_font || "Outfit";
                          const color = z.default_color || "#1c1917";
                          const manual = z.manual_font_size || 0;
                          const fs = manual > 0 ? manual : computeFontSize(txt, z.width, z.height, font, z.min_font_size || 12, z.max_font_size || 240);
                          const curve = z.curve || 0;
                          if (Math.abs(curve) > 5) {
                            return <CurvedText key={`${z.id}-${fs}-${curve}`} text={txt} width={z.width} height={z.height} curve={curve} fontFamily={font} fontSize={fs} fill={color}/>;
                          }
                          return <Text key={`${z.id}-${fs}`} width={z.width} height={z.height} align="center" verticalAlign="middle"
                            text={txt} fontFamily={font} fontSize={fs} fill={color}
                            wrap="word" lineHeight={LINE_HEIGHT} ellipsis={true} listening={false}/>;
                        })()}
                      </Group>
                    ))}
                    <Transformer ref={trRef} rotateEnabled={false} borderStroke="#1c1917" borderStrokeWidth={2} anchorStroke="#1c1917" anchorFill="#fde047"/>
                  </Layer>
                </Stage>
              </div>
            </div>
            <p className="text-xs text-stone-600 mt-2">Tip: click a layer to select. Drag to move, corners to resize.</p>
          </div>

          {/* INSPECTOR */}
          <div className="space-y-4">
            <div className="brutal-card p-4">
              <div className="text-xs font-bold uppercase tracking-widest mb-2">Product base ({view?.name})</div>
              {view?.base_image_url && (
                <img src={view.base_image_url} alt="base" className="w-full aspect-square object-cover border-2 border-stone-900 rounded mb-3"/>
              )}
              <label className="brutal-btn bg-white px-3 py-2 rounded-md text-sm flex items-center justify-center gap-1 cursor-pointer" data-testid="upload-base-label">
                <Upload className="w-4 h-4"/> {uploading ? "Uploading…" : "Upload product photo"}
                <input type="file" accept="image/*" className="hidden" onChange={uploadBase} data-testid="upload-base-input"/>
              </label>
              <div className="mt-3">
                <label className="text-xs font-bold uppercase tracking-widest">Background color</label>
                <input type="color" value={view?.bg_color || "#ffffff"} onChange={(e) => setViewField("bg_color", e.target.value)} className="w-full h-10 border-2 border-stone-900 rounded mt-1"/>
              </div>
            </div>

            <div className="brutal-card p-4">
              <div className="text-xs font-bold uppercase tracking-widest mb-2">Custom fonts</div>
              <label className="brutal-btn bg-white px-3 py-2 rounded-md text-sm flex items-center justify-center gap-1 cursor-pointer">
                <Upload className="w-4 h-4"/> Upload .ttf / .otf / .woff
                <input type="file" accept=".ttf,.otf,.woff,.woff2,font/ttf,font/otf" className="hidden" onChange={uploadFont} data-testid="upload-font-input"/>
              </label>
              {customFontList.length > 0 && (
                <div className="mt-3 space-y-1">
                  {customFontList.map((f) => (
                    <div key={f.id} className="flex items-center justify-between text-sm">
                      <span style={{ fontFamily: f.name }}>{f.name}</span>
                      <button onClick={() => deleteFont(f.id)} className="opacity-60 hover:opacity-100"><X className="w-3 h-3"/></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="brutal-card p-4">
              <div className="text-xs font-bold uppercase tracking-widest mb-2">Ink slots / restricted colors</div>
              <p className="text-xs text-stone-600 mb-3">Give customers a labeled color choice (e.g. &ldquo;Ink pad color&rdquo;) with only allowed colors.</p>
              <div className="space-y-3">
                {(tpl.ink_slots || []).map((slot, si) => (
                  <div key={slot.id} className="border-2 border-stone-900 rounded p-2" data-testid={`ink-slot-${si}`}>
                    <div className="flex items-center gap-2">
                      <input
                        className="brutal-input flex-1 text-sm"
                        value={slot.label}
                        placeholder="Label (e.g. Ink pad color)"
                        onChange={(e) => setField("ink_slots", tpl.ink_slots.map((s, i) => i === si ? { ...s, label: e.target.value } : s))}
                        data-testid={`ink-slot-label-${si}`}/>
                      <button className="opacity-70 hover:opacity-100" onClick={() => setField("ink_slots", tpl.ink_slots.filter((_, i) => i !== si))} title="Delete slot">
                        <X className="w-4 h-4"/>
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {(slot.colors || []).map((c, ci) => (
                        <div key={ci} className="relative">
                          <input
                            type="color"
                            value={c}
                            onChange={(e) => setField("ink_slots", tpl.ink_slots.map((s, i) => i === si ? { ...s, colors: s.colors.map((cc, j) => j === ci ? e.target.value : cc) } : s))}
                            className="w-8 h-8 border-2 border-stone-900 rounded cursor-pointer"/>
                          <button
                            onClick={() => setField("ink_slots", tpl.ink_slots.map((s, i) => i === si ? { ...s, colors: s.colors.filter((_, j) => j !== ci) } : s))}
                            className="absolute -top-1 -right-1 bg-white border border-stone-900 rounded-full w-4 h-4 flex items-center justify-center text-[10px] leading-none">×</button>
                        </div>
                      ))}
                      <button
                        onClick={() => setField("ink_slots", tpl.ink_slots.map((s, i) => i === si ? { ...s, colors: [...(s.colors || []), "#000000"] } : s))}
                        className="w-8 h-8 border-2 border-dashed border-stone-900 rounded text-xs">+</button>
                    </div>
                  </div>
                ))}
                <button
                  onClick={() => setField("ink_slots", [...(tpl.ink_slots || []), { id: `slot-${uid()}`, label: "Color", colors: ["#000000", "#0000ff", "#ff0000"], default_color: "#000000" }])}
                  className="brutal-btn bg-white px-3 py-2 rounded-md text-sm w-full flex items-center justify-center gap-1"
                  data-testid="add-ink-slot">
                  <Plus className="w-4 h-4"/> Add ink slot
                </button>
              </div>
            </div>

            <div className="brutal-card p-4">
              <div className="text-xs font-bold uppercase tracking-widest mb-2">Customer-facing settings</div>
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-bold uppercase">Assigned product page URL</label>
                  <input className="brutal-input w-full text-xs" value={tpl.product_url || ""} onChange={(e) => setField("product_url", e.target.value)} placeholder="https://your-site.com/product/…" data-testid="theme-product-url"/>
                  <p className="text-[11px] text-stone-500 mt-1">The smart embed uses this URL (or the SKU below) to know which product this template belongs to.</p>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase">Product SKU (optional)</label>
                  <input className="brutal-input w-full text-xs" value={tpl.product_sku || ""} onChange={(e) => setField("product_sku", e.target.value)} placeholder="e.g. B0FBNCYXSC" data-testid="theme-product-sku"/>
                  <p className="text-[11px] text-stone-500 mt-1">Set the same SKU as in your Shopify product. Recommended — exact, per-variant matching (no theme code needed for Method A).</p>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase">Notice for customer</label>
                  <textarea rows={2} className="brutal-input w-full" value={tpl.notice || ""} onChange={(e) => setField("notice", e.target.value)}
                    placeholder="e.g. Text align will be auto-adjusted before final print." data-testid="tpl-notice"/>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase">Max text length (per text layer)</label>
                  <input
                    type="number" min={0} max={500}
                    className="brutal-input w-full text-xs"
                    value={tpl.max_text_limit ?? 0}
                    onChange={(e) => setField("max_text_limit", Math.max(0, parseInt(e.target.value || "0", 10)))}
                    placeholder="0 = unlimited"
                    data-testid="tpl-max-text-limit"
                  />
                  <p className="text-[11px] text-stone-500 mt-1">Hard-caps the customer&apos;s text input length on every editable text layer. Live counter shown to customer. Leave <b>0</b> for unlimited.</p>
                </div>
                {tpl.category === "face_stamp" && (
                  <div>
                    <label className="text-xs font-bold uppercase">Photo filter</label>
                    <p className="text-[11px] text-stone-500 mb-1">Pick which filters the buyer gets to choose from at upload time. &ldquo;No effect&rdquo; is always available so buyers are never stuck with zero options.</p>
                    <div className="grid grid-cols-2 gap-1.5">
                      {FACE_FILTERS.map((f) => {
                        const enabled = (tpl.face_stamp_filters || []).includes(f.key);
                        const locked = f.key === "no_effect";
                        return (
                          <label key={f.key} className={`flex items-center gap-2 text-xs border-2 border-stone-900 rounded px-2 py-1.5 ${enabled ? "bg-fuchsia-100" : "bg-white"} ${locked ? "opacity-70" : ""}`}>
                            <input
                              type="checkbox"
                              checked={enabled}
                              disabled={locked}
                              onChange={(e) => {
                                const cur = tpl.face_stamp_filters || [];
                                const next = e.target.checked ? [...cur, f.key] : cur.filter((k) => k !== f.key);
                                setField("face_stamp_filters", next);
                              }}
                              data-testid={`tpl-face-filter-${f.key}`}
                            />
                            {f.label}{locked ? " (always on)" : ""}
                          </label>
                        );
                      })}
                    </div>
                    <p className="text-[11px] text-stone-500 mt-1">Applied client-side the moment the customer picks a filter after uploading their photo — nothing is sent to an AI service.</p>
                  </div>
                )}
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={tpl.whatsapp_required !== false} onChange={(e) => setField("whatsapp_required", e.target.checked)} data-testid="whatsapp-required"/>
                  Require WhatsApp number at checkout
                </label>
              </div>
            </div>

            <div className="brutal-card p-4">
              <div className="text-xs font-bold uppercase tracking-widest mb-2">Layer settings</div>
              {!selected ? (
                <p className="text-sm text-stone-600">Select a layer on the canvas to edit its settings.</p>
              ) : (
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-bold uppercase">Label</label>
                    <input className="brutal-input w-full" value={selected.label || ""} onChange={(e) => setZone(selected.id, { label: e.target.value })} data-testid="zone-label"/>
                  </div>
                  <div className={`p-2 rounded border-2 ${selected.customer_editable === false ? 'bg-slate-100 border-slate-400' : 'bg-yellow-50 border-stone-900'}`}>
                    <label className="flex items-center gap-2 text-sm font-bold">
                      <input type="checkbox" checked={selected.customer_editable !== false}
                        onChange={(e) => setZone(selected.id, { customer_editable: e.target.checked })}
                        data-testid="zone-customer-editable"/>
                      Customer layer (visible in customer editor)
                    </label>
                    <div className="text-xs text-stone-600 mt-1">
                      {selected.customer_editable === false
                        ? "🔒 Backend layer — rendered on the design but hidden from the customer."
                        : "👁 Customer-facing — the buyer sees & can edit this layer."}
                    </div>
                  </div>
                  {selected.type === "text" && (
                    <>
                      <div>
                        <label className="text-xs font-bold uppercase">Default text</label>
                        <input className="brutal-input w-full" value={selected.default_text || ""} onChange={(e) => setZone(selected.id, { default_text: e.target.value })} data-testid="zone-default-text"/>
                      </div>
                      <div>
                        <label className="text-xs font-bold uppercase">Default font</label>
                        <select className="brutal-input w-full" value={selected.default_font || "Outfit"} onChange={(e) => setZone(selected.id, { default_font: e.target.value })}>
                          {allFontOptions.map((f) => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-bold uppercase">Default color</label>
                        <input type="color" className="w-full h-10 border-2 border-stone-900 rounded" value={selected.default_color || "#1c1917"} onChange={(e) => setZone(selected.id, { default_color: e.target.value })}/>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-xs font-bold uppercase">Min size</label>
                          <input type="number" className="brutal-input w-full" value={selected.min_font_size} onChange={(e) => setZone(selected.id, { min_font_size: +e.target.value })}/>
                        </div>
                        <div>
                          <label className="text-xs font-bold uppercase">Max size</label>
                          <input type="number" className="brutal-input w-full" value={selected.max_font_size} onChange={(e) => setZone(selected.id, { max_font_size: +e.target.value })}/>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-bold uppercase">Fixed size (0 = auto-fit)</label>
                        <input type="number" className="brutal-input w-full" value={selected.manual_font_size || 0} onChange={(e) => setZone(selected.id, { manual_font_size: +e.target.value })} data-testid="zone-manual-size"/>
                      </div>
                      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={selected.allow_curve} onChange={(e) => setZone(selected.id, { allow_curve: e.target.checked })}/> Allow customer to change curve</label>
                      <div>
                        <label className="text-xs font-bold uppercase">Default curve: {selected.curve || 0}</label>
                        <input type="range" min={-100} max={100} value={selected.curve || 0}
                          onChange={(e) => setZone(selected.id, { curve: +e.target.value })}
                          className="w-full" data-testid="zone-curve"/>
                        <div className="flex gap-1 mt-1">
                          <button type="button" onClick={() => setZone(selected.id, { curve: -60 })} className="brutal-btn bg-white px-2 py-1 rounded text-xs flex-1">Frown</button>
                          <button type="button" onClick={() => setZone(selected.id, { curve: 0 })} className="brutal-btn bg-white px-2 py-1 rounded text-xs flex-1">Flat</button>
                          <button type="button" onClick={() => setZone(selected.id, { curve: 60 })} className="brutal-btn bg-yellow-200 px-2 py-1 rounded text-xs flex-1">Smile</button>
                        </div>
                      </div>
                      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={selected.allow_font_change} onChange={(e) => setZone(selected.id, { allow_font_change: e.target.checked })}/> Allow font change</label>
                      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={selected.allow_color_change} onChange={(e) => setZone(selected.id, { allow_color_change: e.target.checked })}/> Allow color change</label>
                      {(tpl.ink_slots || []).length > 0 && (
                        <div>
                          <label className="text-xs font-bold uppercase">Restrict color to ink slot</label>
                          <select className="brutal-input w-full" value={selected.ink_slot_id || ""} onChange={(e) => setZone(selected.id, { ink_slot_id: e.target.value })} data-testid="zone-ink-slot">
                            <option value="">— Any color (uses template palette) —</option>
                            {tpl.ink_slots.map((s) => <option key={s.id} value={s.id}>{s.label} ({(s.colors || []).length} colors)</option>)}
                          </select>
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-xs font-bold uppercase">Max lines</label>
                          <input type="number" min={1} max={20} className="brutal-input w-full text-xs" value={selected.max_lines ?? 1}
                            onChange={(e) => setZone(selected.id, { max_lines: Math.max(1, parseInt(e.target.value || "1", 10)) })}
                            data-testid="zone-max-lines"/>
                        </div>
                        <div>
                          <label className="text-xs font-bold uppercase">Max chars / line</label>
                          <input type="number" min={0} max={500} className="brutal-input w-full text-xs" value={selected.max_chars_per_line ?? 0}
                            onChange={(e) => setZone(selected.id, { max_chars_per_line: Math.max(0, parseInt(e.target.value || "0", 10)) })}
                            placeholder="0 = unlimited" data-testid="zone-max-chars"/>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-bold uppercase">Rotation</label>
                        <select className="brutal-input w-full text-xs" value={selected.rotation ?? 0}
                          onChange={(e) => setZone(selected.id, { rotation: parseInt(e.target.value, 10) })}
                          data-testid="zone-rotation">
                          <option value={0}>Horizontal (0°)</option>
                          <option value={-90}>90° counter-clockwise</option>
                          <option value={90}>90° clockwise</option>
                          <option value={180}>180° flipped</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-bold uppercase">Allowed fonts (comma-separated; empty = all)</label>
                        <input type="text" className="brutal-input w-full text-xs" value={(selected.allowed_fonts || []).join(", ")}
                          onChange={(e) => setZone(selected.id, { allowed_fonts: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                          placeholder="e.g. Outfit, Bebas Neue, Lobster" data-testid="zone-allowed-fonts"/>
                      </div>
                      <div>
                        <label className="text-xs font-bold uppercase">Allowed colors (hex, comma-separated; empty = free/ink-slot)</label>
                        <input type="text" className="brutal-input w-full text-xs" value={(selected.allowed_colors || []).join(", ")}
                          onChange={(e) => setZone(selected.id, { allowed_colors: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                          placeholder="e.g. #000000, #dc2626" data-testid="zone-allowed-colors"/>
                      </div>
                      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={selected.allow_reset !== false} onChange={(e) => setZone(selected.id, { allow_reset: e.target.checked })} data-testid="zone-allow-reset"/> Allow reset to default</label>
                      <div className="pt-2 border-t border-stone-300 mt-2">
                        <div className="text-[11px] font-bold uppercase tracking-widest text-stone-600 mb-1">Buyer canvas controls</div>
                        <div className="grid grid-cols-1 gap-1">
                          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!selected.buyer_can_move} onChange={(e) => setZone(selected.id, { buyer_can_move: e.target.checked })} data-testid="zone-buyer-can-move"/> Buyer can drag on canvas</label>
                          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!selected.buyer_can_resize} onChange={(e) => setZone(selected.id, { buyer_can_resize: e.target.checked })} data-testid="zone-buyer-can-resize"/> Buyer can resize font (respects min/max size)</label>
                          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!selected.buyer_can_rotate} onChange={(e) => setZone(selected.id, { buyer_can_rotate: e.target.checked })} data-testid="zone-buyer-can-rotate"/> Buyer can freely rotate 0-360°</label>
                        </div>
                      </div>
                    </>
                  )}
                  {selected.type === "image" && (
                    <>
                      <div>
                        <label className="text-xs font-bold uppercase">Default image (locked unless upload allowed)</label>
                        {selected.default_image_url && (
                          <img src={selected.default_image_url} alt="default" className="w-full aspect-square object-contain border-2 border-stone-900 rounded mt-1 bg-stone-100"/>
                        )}
                        <label className="brutal-btn bg-white px-3 py-2 rounded-md text-sm flex items-center justify-center gap-1 cursor-pointer mt-2">
                          <Upload className="w-4 h-4"/> {selected.default_image_url ? "Replace image" : "Upload default image"}
                          <input type="file" accept="image/*" className="hidden" data-testid="zone-default-image-input" onChange={async (e) => {
                            const file = e.target.files?.[0]; if (!file) return;
                            try {
                              const fd = new FormData(); fd.append("file", file); fd.append("kind", "image");
                              const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                              setZone(selected.id, { default_image_url: fileUrl(data.url) });
                              toast.success("Layer image set");
                            } catch { toast.error("Upload failed"); }
                          }}/>
                        </label>
                        {selected.default_image_url && (
                          <button onClick={() => setZone(selected.id, { default_image_url: "" })} className="brutal-btn bg-red-200 w-full mt-2 py-1.5 rounded text-xs">Remove default image</button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={selected.allow_upload !== false} onChange={(e) => setZone(selected.id, { allow_upload: e.target.checked })} data-testid="zone-allow-upload"/> Upload</label>
                        <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={selected.allow_replace !== false} onChange={(e) => setZone(selected.id, { allow_replace: e.target.checked })} data-testid="zone-allow-replace"/> Replace</label>
                        <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={selected.allow_zoom !== false} onChange={(e) => setZone(selected.id, { allow_zoom: e.target.checked })} data-testid="zone-allow-zoom"/> Zoom</label>
                        <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={selected.allow_move !== false} onChange={(e) => setZone(selected.id, { allow_move: e.target.checked })} data-testid="zone-allow-move"/> Move / Crop</label>
                        <label className="flex items-center gap-2 text-xs col-span-2"><input type="checkbox" checked={selected.allow_reset !== false} onChange={(e) => setZone(selected.id, { allow_reset: e.target.checked })} data-testid="zone-allow-reset-img"/> Reset to default</label>
                      </div>
                      <div>
                        <label className="text-xs font-bold uppercase">Min image side (px)</label>
                        <input type="number" className="brutal-input w-full" value={selected.min_image_dimension || 300} onChange={(e) => setZone(selected.id, { min_image_dimension: +e.target.value })} data-testid="zone-min-image"/>
                      </div>
                    </>
                  )}
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>x: {Math.round(selected.x)}</div>
                    <div>y: {Math.round(selected.y)}</div>
                    <div>w: {Math.round(selected.width)}</div>
                    <div>h: {Math.round(selected.height)}</div>
                  </div>
                  <button onClick={() => { removeZone(selected.id); setSelectedId(null); }} className="brutal-btn bg-red-200 w-full py-2 rounded-md text-sm flex items-center justify-center gap-1" data-testid="delete-zone">
                    <Trash2 className="w-4 h-4"/> Delete layer
                  </button>
                </div>
              )}
            </div>

            <div className="brutal-card p-4">
              <div className="text-xs font-bold uppercase tracking-widest mb-2">Layers ({view?.zones?.length || 0})</div>
              {(() => {
                const zones = view?.zones || [];
                const customer = zones.filter((z) => z.customer_editable !== false);
                const backend = zones.filter((z) => z.customer_editable === false);
                return (
                  <>
                    {customer.length > 0 && (
                      <div className="mb-2">
                        <div className="text-[10px] font-bold uppercase text-stone-500 mb-1">Customer layers ({customer.length})</div>
                        <div className="space-y-1">
                          {customer.map((z) => (
                            <button key={z.id} onClick={() => setSelectedId(z.id)}
                              className={`w-full text-left px-2 py-1 border-2 border-stone-900 rounded text-sm ${selectedId === z.id ? 'bg-yellow-200' : 'bg-white'}`}
                              data-testid={`zone-list-${z.id}`}>
                              {z.type === "text" ? "📝" : "🖼"} {z.label || z.type}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {backend.length > 0 && (
                      <div>
                        <div className="text-[10px] font-bold uppercase text-stone-500 mb-1">Backend layers ({backend.length})</div>
                        <div className="space-y-1">
                          {backend.map((z) => (
                            <button key={z.id} onClick={() => setSelectedId(z.id)}
                              className={`w-full text-left px-2 py-1 border-2 border-slate-400 rounded text-sm ${selectedId === z.id ? 'bg-slate-200' : 'bg-slate-50'}`}
                              data-testid={`zone-list-${z.id}`}>
                              🔒 {z.type === "text" ? "📝" : "🖼"} {z.label || z.type}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {zones.length === 0 && <div className="text-xs text-stone-500">No layers yet. Add one above.</div>}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function BaseImageInZone({ url, width, height }) {
  const [img] = useImage(url, "anonymous");
  if (!img) return null;
  const iw = img.width, ih = img.height;
  const scale = Math.min(width / iw, height / ih);
  const w = iw * scale, h = ih * scale;
  return <KImage image={img} width={w} height={h} x={(width - w) / 2} y={(height - h) / 2} listening={false}/>;
}
